import type { ReactElement } from 'react'
import { Button, Chip } from './ui/index.ts'
import { lock, useIsUnlocked } from '../lib/vault-unlock.ts'

/**
 * The header's lock control: what this browser is holding, and the button
 * that lets it go.
 *
 * It used to be a bare "Lock" with a comment conceding that an operator
 * cannot tell whether this tab still has the key, and that pressing a
 * button they did not need was the cheapest way to find out. ADR 0009
 * makes that answer matter far more than it used to — the key now
 * survives a reload, so "is it unlocked?" is no longer answerable from
 * "did I paste something this session?" — so the state is on screen
 * beside the button instead of being something you discover by acting.
 *
 * The chip is not decoration and it is not colour-only: the word changes
 * with the state, so it reads correctly for a deuteranope, in monochrome,
 * and to a screen reader. `live` for unlocked is the same tone this app
 * uses for a running session; `neutral` for locked, because locked is the
 * resting state and not a problem.
 *
 * **The button stays enabled while locked.** Three reasons, and none of
 * them is laziness:
 *
 *   - `lock()` is idempotent, so the worst case is nothing happening.
 *   - It is a cross-tab broadcast (`vault-unlock-channel.ts`), and the
 *     channel can fail quietly — a tab that believes it is locked is
 *     still the right place to tell the other three to let go.
 *   - It is the control an operator reaches for when something feels
 *     wrong. A disabled Lock button in that moment is an argument with
 *     someone who is already worried.
 *
 * The shell renders this only on `/vault/*` (see `isVaultPath`), because
 * that is where the key is in use and a lock control on the Alerts page
 * would be a question with no context.
 *
 * **What Lock does NOT do, which the tooltip used to get wrong.** It said
 * "forget the tenant key held by this browser". It does not: `lock()`
 * drops the key from every TAB and deliberately leaves the stored record
 * alone (see its docblock), so on a device that was told to remember the
 * key the very next page load re-adopts it with no gesture. An operator
 * who pressed Lock on a shared machine and walked away on the strength of
 * that sentence would have been wrong about the thing that mattered, so
 * the wording now says "until this page is loaded again" out loud.
 *
 * **The pair.** The other half — dropping the stored record — is
 * "Forget this device" on the Tenant key page (`VaultForgetDevice`).
 * Until that existed, ticking "remember on this device" was a one-way
 * decision from inside the admin panel and this tooltip could only warn
 * about it; now it can point at the way out, which is the difference
 * between a caveat and an instruction.
 *
 * The two are deliberately scoped differently and the wording is what
 * makes that legible, because a chip and a button cannot:
 *
 *   - Lock is "for now". Every tab, right away, this session.
 *   - Forget is "stop remembering". This browser's stored key, until
 *     someone pastes the recovery file here again.
 *
 * Lock stays here in the header — it is the panic control and must be
 * one reach away from every vault page. Forget stays on the Tenant key
 * page and nowhere else: it is rare, it is destructive, and a
 * confirm-modal control duplicated into the chrome is a control someone
 * eventually hits by accident.
 */

export function VaultLockButton(): ReactElement {
  const isUnlocked = useIsUnlocked()

  return (
    <div className="flex items-center gap-1.5">
      <Chip tone={isUnlocked ? 'live' : 'neutral'}>
        {isUnlocked ? 'Unlocked' : 'Locked'}
      </Chip>
      <Button
        size="sm"
        title={
          isUnlocked
            ? 'Drop the tenant key from every tab, for now. A browser ' +
              'set to remember it loads it again on the next page load — ' +
              'use “Forget this device” on the Tenant key page to stop that.'
            : 'Already locked — press to tell every other tab to let go too'
        }
        onClick={() => {
          lock()
        }}
      >
        Lock
      </Button>
    </div>
  )
}
